import React, { createContext, useCallback, useContext, useState } from "react";

// Create the Form Calculation Context
const FormCalculationContext = createContext();

/**
 * FormCalculationProvider component that manages table calculations and related state
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export const FormCalculationProvider = ({ children }) => {
  const [tableCalculations, setTableCalculations] = useState({});
  const [expressionEvaluations, setExpressionEvaluations] = useState({});

  /**
   * Update table calculations for a specific table element
   */
  const updateTableCalculations = useCallback((elementId, calculations) => {
    setTableCalculations((prev) => ({
      ...prev,
      [elementId]: calculations,
    }));
  }, []);

  /**
   * Update expression evaluation for a specific workflow variable
   */
  const updateExpressionEvaluation = useCallback((workflowVariableId, value) => {
    setExpressionEvaluations((prev) => ({
      ...prev,
      [workflowVariableId]: value,
    }));
  }, []);

  /**
   * Get calculation value for a specific table and column
   * Can be called with either:
   * 1. (elementId, columnId, calculationType) - for direct table access
   * 2. (workflowVariableId) - for hidden workflow variables linked to tables or expressions
   */
  const getCalculationValue = useCallback(
    (elementIdOrWorkflowVariableId, columnId, calculationType) => {
      // If called with 3 parameters, use direct table access
      if (columnId !== undefined && calculationType !== undefined) {
        const tableCalc = tableCalculations[elementIdOrWorkflowVariableId];
        if (!tableCalc || !tableCalc[columnId]) return null;
        return tableCalc[columnId][calculationType] || null;
      }

      // If called with 1 parameter, find by workflow variable ID
      const workflowVariableId = elementIdOrWorkflowVariableId;

      /* check expression evaluations*/
      if (expressionEvaluations[workflowVariableId] !== undefined) {
        return expressionEvaluations[workflowVariableId];
      }

      for (const [tableId, tableCalc] of Object.entries(tableCalculations)) {
        for (const [colId, calculation] of Object.entries(tableCalc)) {
          if (calculation.linkedVariableId === workflowVariableId) {
            return calculation.value;
          }
        }
      }

      return null;
    },
    [tableCalculations, expressionEvaluations]
  );

  /**
   * Clear calculations for a specific table element
   */
  const clearTableCalculations = useCallback((elementId) => {
    setTableCalculations((prev) => {
      const newCalculations = { ...prev };
      delete newCalculations[elementId];
      return newCalculations;
    });
  }, []);

  /**
   * Clear all calculations
   */
  const clearAllCalculations = useCallback(() => {
    setTableCalculations({});
    setExpressionEvaluations({});
  }, []);

  /**
   * Clear expression evaluations
   */
  const clearExpressionEvaluations = useCallback(() => {
    setExpressionEvaluations({});
  }, []);

  // Context value object
  const value = {
    tableCalculations,
    updateTableCalculations,
    expressionEvaluations,
    updateExpressionEvaluation,
    getCalculationValue,
    clearTableCalculations,
    clearAllCalculations,
    clearExpressionEvaluations,
  };

  return <FormCalculationContext.Provider value={value}>{children}</FormCalculationContext.Provider>;
};

/**
 * Custom hook to use the Form Calculation Context
 * @returns {Object} Form calculation context value
 * @throws {Error} If used outside of FormCalculationProvider
 */
export const useFormCalculation = () => {
  const context = useContext(FormCalculationContext);

  if (context === undefined) {
    throw new Error("useFormCalculation must be used within a FormCalculationProvider");
  }

  return context;
};

export default FormCalculationContext;
